/**
 * Embedded mode — the Compass SPA inside the mobile app's WebView.
 *
 * The Compass App (compass-app/, a hybrid shell) opens most Compass features
 * as the real web page in a WebView. The page then renders without its own
 * chrome (Sidebar, Header) because the shell provides navigation, and it tells
 * the shell which route it is showing so the native header and the shell's
 * sign-in recovery can follow client-side navigation (a WebView only sees
 * real navigations, not pushState).
 *
 * Detection, either of:
 *   - `?embedded=1` on the URL the hand-off redirect lands on
 *     (src/auth/web-session-routes.ts appends it; the literal mirrors
 *     EMBEDDED_QUERY_PARAM in src/contracts/app-catalog.ts) — remembered in
 *     sessionStorage so client-side navigation keeps the mode;
 *   - `window.__compassShell`, injected by the WebView before the page loads.
 *
 * Messages to the shell go through `window.ReactNativeWebView.postMessage`
 * as JSON. Outside the WebView `postToShell` is a no-op that returns false.
 *
 * Ask CompassAI: inside the app the web chat panels never open. Every pill and
 * section "Ask AI" chip hands off to the app's native CompassAI tab instead
 * (`askInShell`), which answers globally — anything the caller's RBAC scope
 * can read, not just the page they were on.
 *
 * Exports: a WebView cannot print (`window.print()` is a no-op / unhandled),
 * open windows, or follow attachment / `blob:` downloads. Inside the app every
 * Export / Download hands the finished document to the shell instead
 * (`printInShell` for PDFs, `saveFileInShell` for HTML/CSV files), which
 * renders or writes it and opens the native share sheet
 * (compass-app/src/lib/shellExport.ts).
 *
 * Share: a link to the page (`shareInShell`) goes to the app's native share
 * sheet — Messages, Mail, Slack, … (compass-app/src/lib/shellShare.ts). Only
 * builds that advertise the `share` capability in `__compassShell` handle it;
 * on older builds the caller falls back to the WebView's own Web Share / copy.
 *
 * Speech: a WebView has no speech recogniser (Android's has no Web Speech API;
 * WKWebView's is not reliable), so when the Meetings live transcript runs on the
 * device the page asks the shell to run the native iOS / Android one
 * (`{type:'speech'}`, `SHELL_SPEECH_EVENT` for the results — see
 * client/src/pages/meetings/shells/shared/shellSpeechLive.ts and
 * compass-app/src/lib/shellSpeech.ts). Only builds advertising `speech` handle it.
 */

const STORAGE_KEY = 'compass:embedded';
const QUERY_PARAM = 'embedded';

export interface ShellInfo {
  platform: 'ios' | 'android';
  appVersion?: string;
  /** Messages this build handles beyond route / ask / print / file (e.g. `share`). */
  capabilities?: string[];
}

/** The file types the shell accepts (mirrors MIME_TYPES in compass-app/src/lib/shellExport.ts). */
export type ShellFileMimeType = 'text/html' | 'text/csv' | 'text/plain' | 'application/json';

export type ShellMessage =
  | { type: 'route'; path: string; title: string }
  /** Open the app's CompassAI tab. `about` is the page subject, `question` a section's pre-filled ask. */
  | { type: 'ask'; path: string; about?: string; question?: string }
  /** A print-ready HTML document → PDF in the share sheet. */
  | { type: 'print'; html: string; title: string }
  /** A text file → the share sheet. */
  | { type: 'file'; filename: string; mimeType: ShellFileMimeType; text: string }
  /** A Compass link → the native share sheet. `title` doubles as the email subject. */
  | { type: 'share'; url: string; title: string; text: string }
  /** Start / stop the native speech recogniser; results arrive as `SHELL_SPEECH_EVENT`. */
  | { type: 'speech'; action: 'start'; lang: string; contextualStrings: string[] }
  | { type: 'speech'; action: 'stop' };

declare global {
  interface Window {
    __compassShell?: ShellInfo;
    ReactNativeWebView?: { postMessage: (data: string) => void };
  }
}

let embedded: boolean | null = null;

/** Resolve (and remember) whether this page runs inside the Compass App. Call once at boot. */
export function initEmbeddedMode(win: Window = window): boolean {
  let flag = false;
  try {
    if (new URLSearchParams(win.location.search).get(QUERY_PARAM) === '1') {
      flag = true;
      win.sessionStorage.setItem(STORAGE_KEY, '1');
    } else if (win.sessionStorage.getItem(STORAGE_KEY) === '1') {
      flag = true;
    }
  } catch {
    // sessionStorage can be unavailable (privacy modes); the shell marker below still works.
  }
  if (win.__compassShell) flag = true;
  embedded = flag;
  if (flag) win.document.documentElement.dataset.embedded = '1';
  return flag;
}

export function isEmbedded(): boolean {
  return embedded ?? initEmbeddedMode();
}

/** Send a message to the native shell. Returns false when there is no shell. */
export function postToShell(message: ShellMessage, win: Window = window): boolean {
  const bridge = win.ReactNativeWebView;
  if (!bridge) return false;
  try {
    bridge.postMessage(JSON.stringify(message));
    return true;
  } catch {
    return false;
  }
}

/**
 * "Ask CompassAI about Baycare" → "Baycare". Deictic subjects ("this dashboard")
 * mean nothing once the user is off the page, so they are dropped.
 */
export function askSubjectFromLabel(label: string | undefined): string | undefined {
  const m = /^ask(?:\s+compassai)?\s+about\s+(.+)$/i.exec(label?.trim() ?? '');
  const subject = m?.[1]?.trim();
  if (!subject || /^(this|these|that)\b/i.test(subject)) return undefined;
  return subject;
}

/**
 * Hand an Ask CompassAI request to the app's native CompassAI tab. Returns
 * false outside the app (or when the bridge is missing) so the caller falls
 * back to opening its web panel.
 */
export function askInShell(opts: { label?: string; question?: string }, win: Window = window): boolean {
  if (!isEmbedded()) return false;
  const question = opts.question?.trim() || undefined;
  return postToShell(
    { type: 'ask', path: win.location.pathname + win.location.search, about: askSubjectFromLabel(opts.label), question },
    win,
  );
}

/**
 * The shell renders the document outside the page, so relative URLs (the
 * app's stylesheet links, /logo.png) need an absolute base to resolve.
 */
function withBaseHref(html: string, origin: string): string {
  if (/<base\s/i.test(html)) return html;
  const base = `<base href="${origin}/">`;
  return /<head[^>]*>/i.test(html) ? html.replace(/<head[^>]*>/i, (m) => `${m}${base}`) : `${base}${html}`;
}

function documentTitle(html: string): string {
  const raw = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html)?.[1] ?? '';
  const el = document.createElement('textarea');
  el.innerHTML = raw; // decode &amp; etc. — a textarea never parses markup
  return el.value.replace(/\s+/g, ' ').trim();
}

/**
 * Inside the app: hand a print-ready HTML document to the shell, which turns
 * it into a PDF and opens the share sheet. Returns false outside the app so
 * the caller falls back to the browser's print dialog.
 */
export function printInShell(html: string, title?: string, win: Window = window): boolean {
  if (!isEmbedded()) return false;
  return postToShell(
    { type: 'print', html: withBaseHref(html, win.location.origin), title: title?.trim() || documentTitle(html) },
    win,
  );
}

/**
 * Inside the app: hand a text file (HTML export, CSV) to the shell's share
 * sheet. Returns false outside the app so the caller falls back to a normal
 * browser download.
 */
export function saveFileInShell(
  filename: string,
  mimeType: ShellFileMimeType,
  text: string,
  win: Window = window,
): boolean {
  if (!isEmbedded()) return false;
  return postToShell({ type: 'file', filename, mimeType, text }, win);
}

function attachmentFilename(header: string | null): string | null {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header ?? '');
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/**
 * Inside the app: fetch a same-origin download (the page's cookie session
 * authorises it, exactly like the browser download would) and hand it to the
 * shell — as a file, or with `print` as a PDF. Returns false outside the app so
 * the caller falls back to a normal browser download.
 */
export function exportUrlInShell(
  url: string,
  opts: { print?: boolean; filename?: string } = {},
  win: Window = window,
): boolean {
  if (!isEmbedded()) return false;
  void (async () => {
    try {
      const res = await win.fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const filename = attachmentFilename(res.headers.get('content-disposition')) ?? opts.filename ?? 'Compass export.html';
      const mime = (res.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase();
      const posted = opts.print
        ? printInShell(text, filename.replace(/\.[a-z0-9]{1,5}$/i, ''), win)
        : saveFileInShell(filename, mime === 'text/csv' ? 'text/csv' : 'text/html', text, win);
      if (!posted) throw new Error('the app did not accept the file');
    } catch (err) {
      win.alert(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  })();
  return true;
}

/**
 * Inside the app: hand a link to the shell's native share sheet. Returns false
 * outside the app, or when this app build predates link sharing, so the caller
 * falls back to the Web Share API / copy link.
 */
export function shareInShell(
  share: { url: string; title: string; text: string },
  win: Window = window,
): boolean {
  if (!isEmbedded() || !win.__compassShell?.capabilities?.includes('share')) return false;
  return postToShell({ type: 'share', url: share.url, title: share.title, text: share.text }, win);
}

/** Does this app build handle `capability` (`__compassShell.capabilities`)? False outside the app. */
export function shellCan(capability: string, win: Window = window): boolean {
  return isEmbedded() && Boolean(win.ReactNativeWebView) && Boolean(win.__compassShell?.capabilities?.includes(capability));
}

/** The window event the shell dispatches with native speech results (mirrors compass-app/src/lib/shellSpeech.ts). */
export const SHELL_SPEECH_EVENT = 'compass:shell-speech';

/** `detail` of a `SHELL_SPEECH_EVENT`. */
export type ShellSpeechEvent =
  /** The words being recognised right now; replaces the previous partial. */
  | { kind: 'partial'; text: string }
  /** A finished utterance. */
  | { kind: 'final'; text: string; confidence: number | null }
  /** `live` once listening; `failed` when the recogniser cannot run (no permission, no on-device model); `stopped` after a stop. */
  | { kind: 'status'; status: 'live' | 'failed' | 'stopped'; message?: string };

/** Test hook. */
export function _resetEmbeddedForTests(): void {
  embedded = null;
}
